/* Replaces the existing coming-soon-banner.js. No external dependencies. */
(() => {
  if (customElements.get('eboot-split-header')) return;
  customElements.define('eboot-split-header', class extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.nav = this.querySelector('.coming-soon__nav');
      this.button = this.querySelector('.coming-soon__menu-toggle');
      this.media = window.matchMedia('(max-width: 1024px)');
      if (this.nav && this.button) {
        this.classList.add('cs-enhanced');
        this.hoverTimers = new Map();
        this.nav.querySelectorAll('details').forEach(dropdown => {
          dropdown.addEventListener('pointerenter', event => {
            if (this.media.matches || event.pointerType !== 'mouse') return;
            clearTimeout(this.hoverTimers.get(dropdown));
            for (const other of this.nav.querySelectorAll('details[open]')) {
              if (other !== dropdown && !other.contains(dropdown)) other.open = false;
            }
            dropdown.open = true;
          }, options);
          dropdown.addEventListener('pointerleave', event => {
            if (this.media.matches || event.pointerType !== 'mouse') return;
            clearTimeout(this.hoverTimers.get(dropdown));
            this.hoverTimers.set(dropdown, setTimeout(() => {
              this.hoverTimers.delete(dropdown);
              if (!dropdown.matches(':hover') && !dropdown.contains(document.activeElement)) {
                dropdown.open = false;
                dropdown.querySelectorAll('details[open]').forEach(child => { child.open = false; });
              }
            }, 160));
          }, options);
        });
        this.button.addEventListener('click', () => this.setOpen(this.button.getAttribute('aria-expanded') !== 'true'), options);
        this.nav.addEventListener('click', event => {
          if (event.target.closest('a')) this.setOpen(false);
        }, options);
        this.addEventListener('keydown', event => {
          if (event.key !== 'Escape') return;
          const openDetails = event.target.closest('details[open]');
          if (openDetails) {
            openDetails.open = false;
            openDetails.querySelector('summary').focus();
          } else if (this.button.getAttribute('aria-expanded') === 'true') {
            this.setOpen(false);
            this.button.focus();
          }
        }, options);
        document.addEventListener('click', event => {
          if (!this.querySelector('.coming-soon__header').contains(event.target)) this.setOpen(false);
        }, options);
        this.nav.addEventListener('focusout', () => {
          queueMicrotask(() => {
            if (!this.isConnected) return;
            if (!this.nav.contains(document.activeElement) && document.activeElement !== this.button) this.setOpen(false);
          });
        }, options);
        /* Close sibling dropdowns while preserving an open parent. */
        this.nav.addEventListener('click', event => {
          const summary = event.target.closest('summary');
          if (!summary) return;
          const details = summary.parentElement;
          if (!this.media.matches && event.detail > 0 && event.pointerType === 'mouse' && details.open && details.matches(':hover')) event.preventDefault();
          for (const other of this.nav.querySelectorAll('details[open]')) {
            if (other !== details && !other.contains(details)) other.open = false;
          }
        }, options);
        this.media.addEventListener('change', () => {
          const focused = document.activeElement;
          this.setOpen(false);
          if (this.media.matches && this.nav.contains(focused)) this.button.focus();
          else if (!this.media.matches && focused === this.button) this.nav.querySelector('a, summary')?.focus();
        }, options);
        this.setOpen(false);
      }
      this.syncOverlay();
      this.resizeObserver = new ResizeObserver(() => this.syncOverlay());
      this.resizeObserver.observe(this);
      document.addEventListener('eboot:layout-changed', () => this.syncOverlay(), options);
    }
    setOpen(open) {
      if (!this.nav || !this.button) return;
      this.button.setAttribute('aria-expanded', String(this.media.matches && open));
      this.nav.hidden = this.media.matches && !open;
      if (!open) { this.hoverTimers?.forEach(timer => clearTimeout(timer)); this.hoverTimers?.clear(); }
      if (!open) this.nav.querySelectorAll('details[open]').forEach(details => { details.open = false; });
    }
    clearOverlay() {
      if (this.overlayBanner) this.overlayBanner.style.removeProperty('--cs-header-height');
      this.overlayBanner = null;
      const wrapper = this.closest('.cs-split-header-section');
      wrapper?.classList.remove('cs-overlay-active');
      wrapper?.style.removeProperty('--cs-header-height');
    }
    syncOverlay() {
      const wrapper = this.closest('.cs-split-header-section');
      if (!wrapper) return;
      const next = wrapper.nextElementSibling;
      const banner = next?.classList.contains('cs-split-banner-section')
        ? next.querySelector('eboot-split-banner') : null;
      const allowed = this.dataset.overlay === 'true' && banner && this.getBoundingClientRect().height > 0;
      if (!allowed) { this.clearOverlay(); return; }
      if (this.overlayBanner && this.overlayBanner !== banner) this.clearOverlay();
      this.overlayBanner = banner;
      const height = Math.ceil(this.getBoundingClientRect().height) + 'px';
      wrapper.style.setProperty('--cs-header-height', height);
      banner.style.setProperty('--cs-header-height', height);
      wrapper.classList.add('cs-overlay-active');
    }
    cleanup() { this.abort?.abort(); this.resizeObserver?.disconnect(); this.clearOverlay(); this.hoverTimers?.forEach(timer => clearTimeout(timer)); this.hoverTimers?.clear(); }
    disconnectedCallback() { this.cleanup(); }
  });
  customElements.define('eboot-split-banner', class extends HTMLElement {
    connectedCallback() { this.disconnectedCallback(); this.initCountdown(); }
    initCountdown() {
      const el = this.querySelector('.coming-soon__countdown');
      if (!el) return;
      const value = (el.dataset.target || '').trim().replace(' ', 'T');
      const target = Date.parse(value);
      const units = ['days', 'hours', 'minutes', 'seconds'].map(unit => el.querySelector('[data-unit="' + unit + '"]'));
      if (!Number.isFinite(target) || units.some(unit => !unit)) { el.hidden = true; return; }
      el.hidden = false;
      const render = () => {
        const seconds = Math.max(0, Math.ceil((target - Date.now()) / 1000));
        const values = [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60];
        units.forEach((unit, index) => { unit.textContent = String(values[index]).padStart(2, '0'); });
        if (seconds === 0) {
          clearInterval(this.timer);
          el.dispatchEvent(new CustomEvent('coming-soon:ended', { bubbles: true }));
          return false;
        }
        return true;
      };
      // An expired timer does not create another interval after its first render.
      if (render()) this.timer = setInterval(render, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  });
  // Handles section addition, removal and reorder in the Theme Editor.
  // Child-list changes only: our style updates do not trigger this observer.
  let scheduled = false;
  const refreshLayout = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      document.dispatchEvent(new Event('eboot:layout-changed'));
    });
  };
  new MutationObserver(records => {
    const sectionChanged = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
      node.nodeType === 1 && (node.matches?.('.shopify-section, eboot-split-header, eboot-split-banner') ||
      node.querySelector?.('eboot-split-header, eboot-split-banner'))
    ));
    if (sectionChanged) refreshLayout();
  }).observe(document.documentElement, { childList: true, subtree: true });
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => document.addEventListener(name, refreshLayout));
  refreshLayout();
})();
